/**
 * Client-side fixture capacity estimates — mirrors backend computerized scheduler:
 * - First kickoff: team arrival + 45 minutes
 * - Gap between fixtures on the same field: 15 minutes
 * - Play window ends at 18:00 each tournament day
 */

export const FIRST_MATCH_AFTER_ARRIVAL_MINUTES = 45;
export const FIXTURE_GAP_MINUTES = 15;
export const DAY_END_MINUTES = 18 * 60;

export function parseTimeToMinutes(timeValue) {
  if (!timeValue || typeof timeValue !== "string") return null;
  const [hourPart, minutePart] = timeValue.split(":");
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return hour * 60 + minute;
}

export function formatMinutesAsTime(totalMinutes) {
  if (totalMinutes == null || Number.isNaN(totalMinutes)) return "—";
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hour = Math.floor(normalized / 60);
  const minute = normalized % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function countTournamentDays({ dateMode, singleDate, startDate, endDate }) {
  if (dateMode === "single") {
    return singleDate ? 1 : 0;
  }
  if (!startDate || !endDate) return 0;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return 0;
  }
  const diffMs = end.getTime() - start.getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
}

/**
 * Estimated fixtures the computerized scheduler will create.
 */
export function estimateRequiredMatches(participantTeams, gameType) {
  const teams = Number(participantTeams) || 0;
  if (teams < 2) return 0;

  if (gameType === "Knock Out Basis") {
    return teams - 1;
  }

  if (gameType === "Group Stages to Knock Out") {
    const groupSize = teams / 2;
    const groupStageMatches = groupSize * (groupSize - 1);
    return groupStageMatches + 3;
  }

  return 0;
}

export function slotsPerFieldPerDay(teamArrivalTime, matchDurationMinutes) {
  const arrivalMinutes = parseTimeToMinutes(teamArrivalTime);
  if (arrivalMinutes == null) return 0;

  const duration = Math.max(15, Number(matchDurationMinutes) || 15);
  const firstKickoff = arrivalMinutes + FIRST_MATCH_AFTER_ARRIVAL_MINUTES;
  if (DAY_END_MINUTES <= firstKickoff) return 0;

  const slotMinutes = duration + FIXTURE_GAP_MINUTES;
  return Math.max(0, Math.floor((DAY_END_MINUTES - firstKickoff) / slotMinutes));
}

export function slotMinutesPerFixture(matchDurationMinutes) {
  return Math.max(15, Number(matchDurationMinutes) || 15) + FIXTURE_GAP_MINUTES;
}

export function buildSchedulingCapacityGuide({
  participantTeams,
  gameType,
  dateMode,
  singleDate,
  startDate,
  endDate,
  venueFieldMode,
  fieldSlotCount,
  teamArrivalTime,
  matchDurationMinutes,
}) {
  const teams = Number(participantTeams) || 0;
  const fields = venueFieldMode === "single" ? 1 : Math.max(2, Number(fieldSlotCount) || 2);
  const days = countTournamentDays({ dateMode, singleDate, startDate, endDate });
  const perFieldPerDay = slotsPerFieldPerDay(teamArrivalTime, matchDurationMinutes);
  const totalCapacity = days * fields * perFieldPerDay;
  const requiredMatches = estimateRequiredMatches(teams, gameType);
  const arrivalMinutes = parseTimeToMinutes(teamArrivalTime);
  const firstKickoffMinutes =
    arrivalMinutes != null ? arrivalMinutes + FIRST_MATCH_AFTER_ARRIVAL_MINUTES : null;

  const hasScheduleInputs =
    teams >= 8 &&
    teams % 2 === 0 &&
    days > 0 &&
    arrivalMinutes != null &&
    Number(matchDurationMinutes) >= 15;

  let status = "incomplete";
  let headline = "Complete schedule fields to preview fixture capacity.";
  const suggestions = [];

  if (hasScheduleInputs) {
    if (perFieldPerDay === 0) {
      status = "insufficient";
      headline =
        "No playable slots fit before 18:00 with this arrival time. Choose an earlier arrival or shorter matches.";
      suggestions.push("Set an earlier team arrival time so the first kickoff (arrival + 45 min) is before 18:00.");
    } else if (totalCapacity < requiredMatches) {
      status = "insufficient";
      headline = `Need ${requiredMatches} fixture slots but only ${totalCapacity} fit with this setup.`;
      const minFields = Math.ceil(requiredMatches / Math.max(1, days * perFieldPerDay));
      const minDays = Math.ceil(requiredMatches / Math.max(1, fields * perFieldPerDay));
      if (minFields > fields) {
        suggestions.push(`Use at least ${minFields} field(s), or add more tournament days.`);
      }
      if (minDays > days) {
        suggestions.push(`Span at least ${minDays} tournament day(s), or add more fields.`);
      }
      if (minFields <= 3 && fields < 3) {
        suggestions.push("Switch to multiple fields if your venue can host parallel matches.");
      }
      suggestions.push(
        `Shorten match duration (currently ${matchDurationMinutes} min + ${FIXTURE_GAP_MINUTES} min gap) to fit more slots per day.`,
      );
    } else if (totalCapacity < requiredMatches * 1.15) {
      status = "tight";
      headline = `Capacity works (${totalCapacity} slots for ${requiredMatches} matches) but leaves little buffer.`;
      suggestions.push("Consider an extra tournament day or one more field for smoother scheduling.");
    } else {
      status = "ready";
      headline = `This setup can host all ${requiredMatches} computerized fixtures comfortably.`;
    }
  }

  return {
    status,
    headline,
    suggestions,
    teams,
    fields,
    days,
    requiredMatches,
    perFieldPerDay,
    totalCapacity,
    slotMinutes: slotMinutesPerFixture(matchDurationMinutes),
    firstKickoffTime: formatMinutesAsTime(firstKickoffMinutes),
    dayEndTime: formatMinutesAsTime(DAY_END_MINUTES),
    hasScheduleInputs,
    canProceed:
      hasScheduleInputs && perFieldPerDay > 0 && totalCapacity >= requiredMatches,
    formatLabel:
      gameType === "Knock Out Basis"
        ? "Knock out"
        : gameType === "Group Stages to Knock Out"
          ? "Group stage → knock out"
          : gameType,
  };
}
